<template>
  <div class="py-10 max-w-4xl mx-auto sm:px-6 lg:px-8">
    <div class="text-center mb-8">
      <h1 class="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
        We'd Love Your Feedback! 💭
      </h1>
      <p class="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
        Your ideas help us build something amazing together. Whether it's a feature you're dreaming of, 
        something that's bugging you, or just a kind word to brighten our day — we're all ears! 🎧
      </p>
    </div>

    <div class="bg-white shadow-xl rounded-2xl border-4 border-gray-600 overflow-hidden" 
         style="box-shadow: inset 0px -6px 0px #9CA3AF, inset 0 -8px 0 0 #F3F4F6, inset 0px 2px 0px 2px #F3F4F6;">
      <div class="p-8">
        <form @submit.prevent="submitFeedback">
          <div class="mb-6">
            <label for="feedback" class="block text-sm font-bold text-gray-900 mb-2">
              What's on your mind? ✨
            </label>
            <textarea
              id="feedback"
              v-model="feedbackText"
              rows="8"
              class="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-colors"
              placeholder="I wish Kairo could... 🚀"
              :disabled="submitting"
              required
            ></textarea>
          </div>

          <div v-if="successMessage" class="mb-4 p-4 bg-green-50 border-2 border-green-500 rounded-lg">
            <p class="text-green-800 font-medium">{{ successMessage }}</p>
          </div>

          <div v-if="errorMessage" class="mb-4 p-4 bg-red-50 border-2 border-red-500 rounded-lg">
            <p class="text-red-800 font-medium">{{ errorMessage }}</p>
          </div>

          <div class="flex items-center justify-between">
            <p class="text-sm text-gray-500">
              Your feedback helps us make Kairo better for everyone 🌟
            </p>
            <button
              type="submit"
              :disabled="submitting || !feedbackText.trim()"
              class="px-6 py-3 text-sm font-bold rounded-lg shadow-sm transition-all duration-200 border-yellow-600 border-2 bg-yellow-200 text-yellow-600 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-50 disabled:opacity-50 disabled:cursor-not-allowed"
              style="box-shadow: inset 0px -6px 0px #FBBF24, inset 0 -7px 0 0 #FFFBEB, inset 0px 1px 0px 1px #FFFBEB;"
            >
              {{ submitting ? 'Sending...' : 'Send Feedback 🚀' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div class="mt-8 text-center">
      <p class="text-sm text-gray-500">
        Prefer a chat? Reach out at <a href="mailto:hello@getkairo.com" class="text-yellow-600 hover:text-yellow-700 font-medium underline">hello@getkairo.com</a>
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'Feedback',
  data() {
    return {
      feedbackText: '',
      submitting: false,
      successMessage: '',
      errorMessage: ''
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser'])
  },
  methods: {
    async submitFeedback() {
      if (!this.feedbackText.trim()) return;

      this.submitting = true;
      this.successMessage = '';
      this.errorMessage = '';

      try {
        // Import the shared Supabase helper
        const { insertIntoSupabase } = await import('@/helpers/supabase-api');
        
        // Insert feedback into Supabase
        await insertIntoSupabase('feedback', {
          feedback_text: this.feedbackText.trim()
        });

        // Track event in EngageKit
        if (typeof window.EngageKit !== 'undefined') {
          window.EngageKit.track('Feedback Form Submitted', {
            feedbackLength: this.feedbackText.trim().length
          });
        }

        this.successMessage = '🎉 Thank you! Your feedback means the world to us. We will read it carefully!';
        this.feedbackText = '';

        // Clear success message after 5 seconds
        setTimeout(() => {
          this.successMessage = '';
        }, 5000);
      } catch (error) {
        console.error('Failed to submit feedback:', error);
        this.errorMessage = 'Oops! Something went wrong. Please try again or email us directly.';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
